import { Flex } from '@mantine/core';
import { type IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { DisplayCreative, Goal, Partner, Targeting, TTDCampaign } from '@feathr/blackbox';
import { Alert, CardV2 as Card } from '@feathr/components';
import {
  countMaskedMetaCreatives,
  getMetaPortraitReviewTitle,
  getMetaPortraitReviewWarning,
} from '@feathr/extender/components/creatives/status.utils';
import { useCampaignChannelVisibility } from '@feathr/extender/hooks';
import type { ListResponse } from '@feathr/rachis';
import { TargetAudienceOverview, useAudienceBreakdown } from '@feathr/report_components';

import type { ICampaignValidationErrors } from '../../../CampaignSummary';
import CampaignSummary from '../../../CampaignSummary';
import {
  getMetaLookalikeLabel,
  valueToSlider,
} from '../AdWizardAudienceStep/LookalikeAudienceTargeting/LookalikeAudienceTargeting.utils';
import TTDRecommendedOptimizationModal from '../TTDRecommendedOptimizationModal';

import * as styles from '../AdWizard.css';

export interface IAdWizardReviewStepProps {
  campaign: TTDCampaign;
  targetings: ListResponse<Targeting>;
  goals: IObservableArray<Goal>;
  creatives: IObservableArray<DisplayCreative>;
  getValidationErrors: () => ICampaignValidationErrors;
  /** Hash link to the Creatives step so the user can fix flagged creatives. */
  creativesStepHref?: string;
}

export function validatePartner(partner: Partner): string[] {
  return partner.validate([], false).errors;
}

function AdWizardReviewStep({
  campaign,
  creatives,
  targetings,
  getValidationErrors,
  creativesStepHref,
}: Readonly<IAdWizardReviewStepProps>): JSX.Element {
  const location = useLocation();
  const { t } = useTranslation();
  const { showDisplay, showMeta } = useCampaignChannelVisibility(campaign);
  const audienceData = useAudienceBreakdown({
    targetings,
    campaign,
    showMeta,
  });

  const sliderPosition = audienceData.lookalikeAudienceSize
    ? valueToSlider(audienceData.lookalikeAudienceSize)
    : undefined;
  const metaLookalikeLabel = getMetaLookalikeLabel(sliderPosition, t);

  const queryParams = new URLSearchParams(location.search);
  const additionalBudgetParam = Number(queryParams.get('additionalBudget'));

  const { isOmniCampaign, isOmniReachCampaign, isPastEndDate, isMonetization, isPublished } =
    campaign;

  // Never prompt the user to increase budget if the campaign has reached its end date
  const showRecommendationsModal =
    !!additionalBudgetParam && !isPastEndDate && !isMonetization && isPublished;

  const errors = getValidationErrors();
  const maskedMetaCount = countMaskedMetaCreatives(creatives);

  return (
    <Flex
      className={isOmniCampaign ? styles.flexRoot : styles.formRoot}
      data-name={'review_step'}
      justify={isOmniCampaign ? 'center' : 'space-between'}
    >
      {showRecommendationsModal && (
        <TTDRecommendedOptimizationModal campaign={campaign} validate={getValidationErrors} />
      )}
      <Card width={'wide'}>
        <Card.Header
          description={t('Review the summary below to make sure everything is set up correctly.')}
          title={t('Review')}
        />
        <Card.Content>
          {maskedMetaCount > 0 && (
            <Alert
              description={getMetaPortraitReviewWarning(t)}
              title={getMetaPortraitReviewTitle(t, maskedMetaCount)}
              type={'warning'}
            >
              {creativesStepHref && <a href={creativesStepHref}>{t('Edit creatives')}</a>}
            </Alert>
          )}
          <CampaignSummary
            campaign={campaign}
            creatives={creatives}
            layout={'vertical'}
            validationErrors={errors}
          />
        </Card.Content>
      </Card>
      {isOmniCampaign && !isOmniReachCampaign && (
        <div className={styles.right}>
          <TargetAudienceOverview
            {...audienceData}
            metaLookalikeLabel={metaLookalikeLabel}
            showDisplay={showDisplay}
            showMeta={showMeta}
          />
        </div>
      )}
    </Flex>
  );
}

export default observer(AdWizardReviewStep);
